import { useState, useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, Award, Shield, Heart, Mail, ExternalLink, CheckCircle2 } from 'lucide-react';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import AdminRetailers from './pages/AdminRetailers';
import AdminDeals from './pages/AdminDeals';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Click tracking function
const trackClick = async (linkType, linkId, linkName, destinationUrl) => {
  try {
    await axios.post(`${API}/track-click`, {
      link_type: linkType,
      link_id: linkId,
      link_name: linkName,
      destination_url: destinationUrl,
      user_agent: navigator.userAgent,
      referrer: document.referrer
    });
  } catch (error) {
    console.error('Error tracking click:', error);
  }
};

const Home = () => {
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState('');
  const [subscribeErrorMessage, setSubscribeErrorMessage] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [retailers, setRetailers] = useState([]);
  const [selectedForComparison, setSelectedForComparison] = useState([]);
  const [showComparison, setShowComparison] = useState(false);

  // Fetch retailers from API
  useEffect(() => {
    fetchRetailers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRetailers = async () => {
    try {
      const response = await axios.get(`${API}/retailers`);
      if (response.data && response.data.length > 0) {
        setRetailers(response.data);
      } else {
        // Fallback to default retailers
        setRetailers(defaultRetailers);
      }
    } catch (error) {
      console.error('Error fetching retailers:', error);
      setRetailers(defaultRetailers);
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setIsSubscribing(true);
    setSubscribeStatus('');
    setSubscribeErrorMessage('');

    try {
      await axios.post(`${API}/newsletter`, { email });
      setSubscribeStatus('success');
      setEmail('');
      setTimeout(() => setSubscribeStatus(''), 5000);
    } catch (error) {
      setSubscribeStatus('error');
      // Extract error message from backend response
      const errorMsg = error.response?.data?.detail || 'An error occurred. Please try again.';
      setSubscribeErrorMessage(errorMsg);
      setTimeout(() => {
        setSubscribeStatus('');
        setSubscribeErrorMessage('');
      }, 5000);
    } finally {
      setIsSubscribing(false);
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const defaultRetailers = [
    { id: '1', name: 'James Allen', description: '360° Diamond Views', affiliate_link: 'https://www.jamesallen.com' },
    { id: '2', name: 'Blue Nile', description: 'Build Your Own Ring', affiliate_link: 'https://www.bluenile.com' },
    { id: '3', name: 'Faithful Platform', description: 'Save on the Ring, Invest in the Marriage.', affiliate_link: 'https://www.faithfulplatform.com' },
    { id: '4', name: 'LEIBISH', description: 'Fancy Color Diamonds', affiliate_link: 'https://www.leibish.com' },
    { id: '5', name: 'Brilliant Earth', description: 'Ethical Sourcing', affiliate_link: 'https://www.brilliantearth.com' },
    { id: '6', name: 'Whiteflash', description: 'A CUT ABOVE®', affiliate_link: 'https://www.whiteflash.com' },
    { id: '7', name: 'Ritani', description: 'Designer Collections', affiliate_link: 'https://www.ritani.com' },
    { id: '8', name: 'Adiamor', description: 'Custom Design', affiliate_link: 'https://www.adiamor.com' },
    { id: '9', name: 'With Clarity', description: 'Lab-Grown Diamonds', affiliate_link: 'https://www.withclarity.com' }
  ];

  // Comparison data for retailers
  const retailerComparisonData = {
    'James Allen': { pricing: '$$-$$$', return_policy: '30 Days', shipping: 'Free', certification: 'GIA, AGS', specialty: '360° HD Videos', warranty: 'Lifetime' },
    'Blue Nile': { pricing: '$$-$$$', return_policy: '30 Days', shipping: 'Free', certification: 'GIA, AGS', specialty: 'Build Your Own', warranty: 'Lifetime' },
    'Faithful Platform': { pricing: '$-$$', return_policy: '30 Days', shipping: 'Free', certification: 'GIA', specialty: 'Value Pricing', warranty: 'Lifetime' },
    'LEIBISH': { pricing: '$$$-$$$$', return_policy: '7 Days', shipping: 'Free $50+', certification: 'GIA, IGI', specialty: 'Fancy Colors', warranty: 'Limited' },
    'Brilliant Earth': { pricing: '$$-$$$', return_policy: '30 Days', shipping: 'Free', certification: 'GIA, AGS', specialty: 'Ethical Sourcing', warranty: 'Lifetime' },
    'Whiteflash': { pricing: '$$$-$$$$', return_policy: '30 Days', shipping: 'Free', certification: 'AGS Ideal', specialty: 'Super Ideal Cut', warranty: 'Lifetime' },
    'Ritani': { pricing: '$$-$$$', return_policy: '30 Days', shipping: 'Free', certification: 'GIA', specialty: 'Designer Settings', warranty: 'Lifetime' },
    'Adiamor': { pricing: '$$', return_policy: '30 Days', shipping: 'Free', certification: 'GIA, AGS', specialty: 'Custom Design', warranty: 'Lifetime' },
    'With Clarity': { pricing: '$-$$', return_policy: '100 Days', shipping: 'Free', certification: 'IGI', specialty: 'Lab-Grown', warranty: 'Lifetime' }
  };

  const toggleComparison = (retailerName) => {
    setSelectedForComparison(prev => {
      if (prev.includes(retailerName)) {
        return prev.filter(name => name !== retailerName);
      } else if (prev.length < 4) {
        return [...prev, retailerName];
      }
      return prev;
    });
  };

  const clearComparison = () => {
    setSelectedForComparison([]);
    setShowComparison(false);
  };

  const categories = [
    {
      title: 'Engagement Rings',
      description: 'Begin your forever with the perfect symbol of love',
      image: 'https://images.unsplash.com/photo-1742240439165-60790db1ee93'
    },
    {
      title: 'Wedding Bands',
      description: 'Timeless bands to celebrate your union',
      image: 'https://images.unsplash.com/photo-1578632297758-3a6c6de14be8'
    },
    {
      title: 'Anniversary Rings',
      description: 'Celebrate your love story with elegance',
      image: 'https://images.unsplash.com/photo-1712311779578-9f25127cdd20'
    }
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Navigation */}
      <nav className="fixed w-full bg-[#050505]/95 backdrop-blur-sm z-50 border-b border-[#D4AF37]/20" data-testid="main-navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <Sparkles className="w-6 h-6 text-[#D4AF37]" />
              <span className="text-2xl font-serif font-bold text-[#D4AF37]">The Perfect Ring</span>
            </div>
            <div className="hidden md:flex space-x-8">
              <button onClick={() => scrollToSection('retailers')} className="text-gray-300 hover:text-[#D4AF37] transition-colors" data-testid="nav-retailers-btn">Retailers</button>
              <button onClick={() => setShowComparison(!showComparison)} className="text-gray-300 hover:text-[#D4AF37] transition-colors relative" data-testid="nav-compare-btn">
                Compare
                {selectedForComparison.length > 0 && (
                  <span className="absolute -top-2 -right-2 bg-[#D4AF37] text-[#050505] text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {selectedForComparison.length}
                  </span>
                )}
              </button>
              <button onClick={() => scrollToSection('categories')} className="text-gray-300 hover:text-[#D4AF37] transition-colors" data-testid="nav-categories-btn">Categories</button>
              <button onClick={() => scrollToSection('education')} className="text-gray-300 hover:text-[#D4AF37] transition-colors" data-testid="nav-education-btn">Education</button>
              <button onClick={() => scrollToSection('newsletter')} className="text-gray-300 hover:text-[#D4AF37] transition-colors" data-testid="nav-newsletter-btn">Newsletter</button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-16 min-h-screen flex items-center" data-testid="hero-section">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#050505]/50 to-[#050505]"></div>
          <img 
            src="https://images.unsplash.com/photo-1742240439165-60790db1ee93" 
            alt="Luxury Diamond Rings" 
            className="w-full h-full object-cover opacity-30"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <h1 className="text-5xl md:text-7xl font-serif font-bold mb-6 text-[#D4AF37]" data-testid="hero-title">
            Discover Your Perfect Ring
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto">
            Compare top diamond retailers for engagement rings, wedding bands, and anniversary jewelry
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => scrollToSection('retailers')} 
              className="bg-[#D4AF37] text-[#050505] px-8 py-4 rounded-lg font-semibold hover:bg-[#C5A028] transition-all transform hover:scale-105"
              data-testid="hero-cta-retailers-btn"
            >
              Browse Retailers
            </button>
            <button 
              onClick={() => scrollToSection('education')} 
              className="border-2 border-[#D4AF37] text-[#D4AF37] px-8 py-4 rounded-lg font-semibold hover:bg-[#D4AF37]/10 transition-all"
              data-testid="hero-cta-education-btn"
            >
              Learn About Diamonds
            </button>
          </div>
        </div>
      </section>

      {/* Top 10 Retailers */}
      <section id="retailers" className="py-20 bg-[#050505]" data-testid="retailers-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-[#D4AF37] mb-4" data-testid="retailers-title">
              Top Diamond Retailers
            </h2>
            <p className="text-xl text-gray-400">Trusted partners for your perfect ring</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {(retailers.length > 0 ? retailers : defaultRetailers).map((retailer, index) => (
              <div
                key={retailer.id || index}
                className="relative bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] p-6 rounded-xl border border-[#D4AF37]/20 hover:border-[#D4AF37] transition-all hover:shadow-xl hover:shadow-[#D4AF37]/20"
                data-testid={`retailer-card-${index}`}
              >
                {/* Comparison Checkbox */}
                <div className="absolute top-3 right-3 z-10 flex items-center gap-2 bg-[#050505]/80 px-2 py-1 rounded">
                  <label className="text-[#D4AF37] text-xs font-semibold cursor-pointer">Compare</label>
                  <input
                    type="checkbox"
                    checked={selectedForComparison.includes(retailer.name)}
                    onChange={(e) => {
                      e.stopPropagation();
                      toggleComparison(retailer.name);
                    }}
                    className="w-5 h-5 accent-[#D4AF37] cursor-pointer"
                    data-testid={`compare-checkbox-${index}`}
                    title="Select to compare"
                  />
                </div>

                <a
                  href={retailer.affiliate_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    e.stopPropagation();
                    trackClick('retailer', retailer.id || index.toString(), retailer.name, retailer.affiliate_link);
                  }}
                  className="block"
                >
                  <h3 className="text-xl font-bold text-[#D4AF37] mb-2">{retailer.name}</h3>
                  <p className="text-gray-400 text-sm">{retailer.description}</p>
                  <div className="mt-4 flex items-center text-[#D4AF37] text-sm font-semibold">
                    Visit Store
                    <ExternalLink className="ml-1 w-4 h-4" />
                  </div>
                </a>
              </div>
            ))}
          </div>

          {/* Comparison Tool Button */}
          {selectedForComparison.length > 0 && (
            <div className="mt-8 text-center">
              <button
                onClick={() => setShowComparison(true)}
                className="bg-[#D4AF37] text-[#050505] px-8 py-4 rounded-lg font-semibold hover:bg-[#C5A028] transition-all transform hover:scale-105"
                data-testid="show-comparison-btn"
              >
                Compare Selected ({selectedForComparison.length})
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Comparison Modal */}
      {showComparison && selectedForComparison.length > 0 && (
        <div className="fixed inset-0 bg-black/80 z-50 overflow-y-auto" data-testid="comparison-modal">
          <div className="min-h-screen px-4 py-8">
            <div className="max-w-7xl mx-auto">
              <div className="bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] rounded-2xl border border-[#D4AF37]/30 p-8">
                {/* Header */}
                <div className="flex justify-between items-center mb-8">
                  <h2 className="text-3xl font-serif font-bold text-[#D4AF37]">Retailer Comparison</h2>
                  <button
                    onClick={() => setShowComparison(false)}
                    className="text-gray-400 hover:text-[#D4AF37] text-3xl font-bold"
                    data-testid="close-comparison-btn"
                  >
                    ×
                  </button>
                </div>

                {/* Comparison Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b border-[#D4AF37]/30">
                        <th className="py-4 px-4 text-[#D4AF37] font-semibold">Feature</th>
                        {selectedForComparison.map((retailerName, idx) => (
                          <th key={idx} className="py-4 px-4 text-[#D4AF37] font-serif text-xl">
                            {retailerName}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-[#D4AF37]/10">
                        <td className="py-4 px-4 text-gray-300 font-semibold">Pricing Range</td>
                        {selectedForComparison.map((retailerName, idx) => (
                          <td key={idx} className="py-4 px-4 text-white">
                            {retailerComparisonData[retailerName]?.pricing || 'N/A'}
                          </td>
                        ))}
                      </tr>
                      <tr className="border-b border-[#D4AF37]/10">
                        <td className="py-4 px-4 text-gray-300 font-semibold">Return Policy</td>
                        {selectedForComparison.map((retailerName, idx) => (
                          <td key={idx} className="py-4 px-4 text-white">
                            {retailerComparisonData[retailerName]?.return_policy || 'N/A'}
                          </td>
                        ))}
                      </tr>
                      <tr className="border-b border-[#D4AF37]/10">
                        <td className="py-4 px-4 text-gray-300 font-semibold">Shipping</td>
                        {selectedForComparison.map((retailerName, idx) => (
                          <td key={idx} className="py-4 px-4 text-white">
                            {retailerComparisonData[retailerName]?.shipping || 'N/A'}
                          </td>
                        ))}
                      </tr>
                      <tr className="border-b border-[#D4AF37]/10">
                        <td className="py-4 px-4 text-gray-300 font-semibold">Certification</td>
                        {selectedForComparison.map((retailerName, idx) => (
                          <td key={idx} className="py-4 px-4 text-white">
                            {retailerComparisonData[retailerName]?.certification || 'N/A'}
                          </td>
                        ))}
                      </tr>
                      <tr className="border-b border-[#D4AF37]/10">
                        <td className="py-4 px-4 text-gray-300 font-semibold">Specialty</td>
                        {selectedForComparison.map((retailerName, idx) => (
                          <td key={idx} className="py-4 px-4 text-white">
                            {retailerComparisonData[retailerName]?.specialty || 'N/A'}
                          </td>
                        ))}
                      </tr>
                      <tr className="border-b border-[#D4AF37]/10">
                        <td className="py-4 px-4 text-gray-300 font-semibold">Warranty</td>
                        {selectedForComparison.map((retailerName, idx) => (
                          <td key={idx} className="py-4 px-4 text-white">
                            {retailerComparisonData[retailerName]?.warranty || 'N/A'}
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <td className="py-4 px-4 text-gray-300 font-semibold">Visit Site</td>
                        {selectedForComparison.map((retailerName, idx) => {
                          const retailer = (retailers.length > 0 ? retailers : defaultRetailers).find(r => r.name === retailerName);
                          return (
                            <td key={idx} className="py-4 px-4">
                              <a
                                href={retailer?.affiliate_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => trackClick('retailer', retailer?.id || '', retailerName, retailer?.affiliate_link || '')}
                                className="inline-flex items-center bg-[#D4AF37] text-[#050505] px-4 py-2 rounded-lg font-semibold hover:bg-[#C5A028] transition-all text-sm"
                              >
                                Visit
                                <ExternalLink className="ml-1 w-4 h-4" />
                              </a>
                            </td>
                          );
                        })}
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Footer Actions */}
                <div className="mt-8 flex justify-between items-center">
                  <button
                    onClick={clearComparison}
                    className="text-gray-400 hover:text-[#D4AF37] transition-colors"
                    data-testid="clear-comparison-btn"
                  >
                    Clear Selection
                  </button>
                  <p className="text-sm text-gray-500">Select up to 4 retailers to compare</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ring Categories */}
      <section id="categories" className="py-20 bg-[#0a0a0a]" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-[#D4AF37] mb-4" data-testid="categories-title">
              Ring Categories
            </h2>
            <p className="text-xl text-gray-400">Find the perfect ring for every occasion</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {categories.map((category, index) => (
              <div 
                key={index}
                className="relative rounded-2xl overflow-hidden group cursor-pointer"
                data-testid={`category-card-${index}`}
              >
                <img 
                  src={category.image} 
                  alt={category.title}
                  className="w-full h-96 object-cover transform group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/50 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="text-2xl font-serif font-bold text-[#D4AF37] mb-2">{category.title}</h3>
                  <p className="text-gray-300">{category.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Diamond Education */}
      <section id="education" className="py-20 bg-[#050505]" data-testid="education-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-[#D4AF37] mb-4" data-testid="education-title">
              Diamond Education
            </h2>
            <p className="text-xl text-gray-400">Everything you need to know before you buy</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="relative rounded-2xl overflow-hidden" data-testid="education-image-clarity">
              <img 
                src="https://images.unsplash.com/photo-1739918192030-2b36e166761a" 
                alt="Diamond Clarity"
                className="w-full h-96 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent"></div>
            </div>
            <div className="relative rounded-2xl overflow-hidden" data-testid="education-image-shapes">
              <img 
                src="https://images.unsplash.com/photo-1762505464446-c0760d740aee" 
                alt="Diamond Shapes"
                className="w-full h-96 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent"></div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] p-6 rounded-xl border border-[#D4AF37]/20" data-testid="education-card-cut">
              <Award className="w-12 h-12 text-[#D4AF37] mb-4" />
              <h3 className="text-2xl font-bold text-[#D4AF37] mb-3">Cut</h3>
              <p className="text-gray-400">The most important factor in a diamond's beauty. Cut determines how well a diamond reflects light, creating its sparkle and brilliance.</p>
            </div>
            <div className="bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] p-6 rounded-xl border border-[#D4AF37]/20" data-testid="education-card-color">
              <Sparkles className="w-12 h-12 text-[#D4AF37] mb-4" />
              <h3 className="text-2xl font-bold text-[#D4AF37] mb-3">Color</h3>
              <p className="text-gray-400">Graded from D (colorless) to Z (light yellow). The less color, the higher the grade and value of the diamond.</p>
            </div>
            <div className="bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] p-6 rounded-xl border border-[#D4AF37]/20" data-testid="education-card-clarity">
              <Shield className="w-12 h-12 text-[#D4AF37] mb-4" />
              <h3 className="text-2xl font-bold text-[#D4AF37] mb-3">Clarity</h3>
              <p className="text-gray-400">Measures internal and external imperfections. Higher clarity grades mean fewer visible flaws and more brilliant sparkle.</p>
            </div>
            <div className="bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] p-6 rounded-xl border border-[#D4AF37]/20" data-testid="education-card-carat">
              <Heart className="w-12 h-12 text-[#D4AF37] mb-4" />
              <h3 className="text-2xl font-bold text-[#D4AF37] mb-3">Carat</h3>
              <p className="text-gray-400">The weight of the diamond. One carat equals 200 milligrams. Larger carats are rarer and more valuable.</p>
            </div>
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] p-8 rounded-xl border border-[#D4AF37]/20" data-testid="education-shapes">
              <h3 className="text-2xl font-bold text-[#D4AF37] mb-4">Diamond Shapes</h3>
              <p className="text-gray-400 mb-4">Popular diamond shapes include:</p>
              <ul className="space-y-2 text-gray-300">
                <li>• <strong>Round Brilliant:</strong> Most popular, maximum sparkle</li>
                <li>• <strong>Princess:</strong> Square shape with brilliant facets</li>
                <li>• <strong>Cushion:</strong> Vintage-inspired soft corners</li>
                <li>• <strong>Oval:</strong> Elongated brilliant cut</li>
                <li>• <strong>Emerald:</strong> Rectangular with step-cut facets</li>
              </ul>
            </div>
            <div className="bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] p-8 rounded-xl border border-[#D4AF37]/20" data-testid="education-certification">
              <h3 className="text-2xl font-bold text-[#D4AF37] mb-4">Certification</h3>
              <p className="text-gray-400 mb-4">Always buy certified diamonds from reputable labs:</p>
              <ul className="space-y-2 text-gray-300">
                <li>• <strong>GIA:</strong> Gemological Institute of America</li>
                <li>• <strong>AGS:</strong> American Gem Society</li>
                <li>• <strong>IGI:</strong> International Gemological Institute</li>
                <li>• Certification ensures authenticity and quality</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section id="newsletter" className="py-20 bg-gradient-to-b from-[#0a0a0a] to-[#050505]" data-testid="newsletter-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Mail className="w-16 h-16 text-[#D4AF37] mx-auto mb-6" />
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-[#D4AF37] mb-4" data-testid="newsletter-title">
            Stay Updated
          </h2>
          <p className="text-xl text-gray-400 mb-8">
            Subscribe to our newsletter for exclusive deals, diamond buying tips, and the latest trends in fine jewelry
          </p>

          <form onSubmit={handleNewsletterSubmit} className="max-w-md mx-auto" data-testid="newsletter-form">
            <div className="flex flex-col sm:flex-row gap-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="flex-1 px-6 py-4 rounded-lg bg-[#1a1a1a] border border-[#D4AF37]/30 text-white placeholder-gray-500 focus:outline-none focus:border-[#D4AF37] transition-all"
                data-testid="newsletter-email-input"
              />
              <button
                type="submit"
                disabled={isSubscribing}
                className="bg-[#D4AF37] text-[#050505] px-8 py-4 rounded-lg font-semibold hover:bg-[#C5A028] transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="newsletter-submit-btn"
              >
                {isSubscribing ? 'Subscribing...' : 'Subscribe'}
              </button>
            </div>

            {subscribeStatus === 'success' && (
              <div className="mt-4 flex items-center justify-center text-green-400" data-testid="newsletter-success-message">
                <CheckCircle2 className="w-5 h-5 mr-2" />
                Successfully subscribed! Check your email for confirmation.
              </div>
            )}

            {subscribeStatus === 'error' && (
              <div className="mt-4 text-red-400" data-testid="newsletter-error-message">
                {subscribeErrorMessage}
              </div>
            )}
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#050505] border-t border-[#D4AF37]/20 py-12" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Sparkles className="w-6 h-6 text-[#D4AF37]" />
                <span className="text-xl font-serif font-bold text-[#D4AF37]">The Perfect Ring</span>
              </div>
              <p className="text-gray-400">Your trusted guide to finding the perfect diamond ring from the world's top retailers.</p>
            </div>
            <div>
              <h4 className="text-lg font-bold text-[#D4AF37] mb-4">Quick Links</h4>
              <ul className="space-y-2 text-gray-400">
                <li><button onClick={() => scrollToSection('deals')} className="hover:text-[#D4AF37] transition-colors">Featured Deals</button></li>
                <li><button onClick={() => scrollToSection('retailers')} className="hover:text-[#D4AF37] transition-colors">Retailers</button></li>
                <li><button onClick={() => scrollToSection('education')} className="hover:text-[#D4AF37] transition-colors">Education</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-bold text-[#D4AF37] mb-4">Disclaimer</h4>
              <p className="text-gray-400 text-sm">We may earn a commission from qualifying purchases made through our affiliate links. This comes at no extra cost to you.</p>
            </div>
          </div>
          <div className="text-center text-gray-500 text-sm pt-8 border-t border-[#D4AF37]/10">
            © 2025 The Perfect Ring. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/admin" element={<AdminLogin />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        <Route path="/admin/retailers" element={<AdminRetailers />} />
        <Route path="/admin/deals" element={<AdminDeals />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;